import { FunctionComponent } from "react";

import get from "lodash/get";
import includes from "lodash/includes";
import dynamic from "next/dynamic";
import Image from "next/image";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import { Text } from "@/components/atoms/Text";
import { cloudinaryImageLoader } from "~/utils/image-loaders";

const categoryIcons: { [key: string]: string } = {
  av_equipment: "Stereo",
  dropoffs_deliveries: "Van",
  experiences: "Experiences",
  extra_space: "ExtraSpace",
  food_drinks: "Utensils",
  furniture: "Chair",
  office_supplies: "OfficeSupplies",
  parking: "Parking",
  party_addons: "PartyAddOns",
  production_equipment: "Camera",
  table_settings: "Utensils",
  "non-alcoholic_drinks": "DrinkNonAlcoholic",
  alcoholic_drinks: "DrinkAlcholic",
  full_bar_package: "DrinkAlcholic",
  bar_services: "DrinkAlcoholic",
  backdrops: "Backdrop",
  lighting: "LightingProd",
  props: "StarFilled",
  grips: "Grip",
  power_supplies: "ElectricalPlug",
  ladder: "Ladder",
};

interface AddOnItemProps {
  item: {
    backgroundImg?: string;
    imgUrl?: string;
    displayCategory?: string;
    displaySubcategory?: string;
    label?: string;
    unit_cost?: number;
    count?: number;
    description?: string;
    category_display_string?: string;
    subcategory_display_string?: string;
    image_ids?: string[];
    currency?: { code: string };
    unit_cost_basis?: string;
    visible_on_listing?: boolean;
    category?: string;
    subcategory?: string;
  };
  listingPageDisplay?: boolean;
}

interface PriceOptionLabel {
  [key: string]: {
    id: string;
    defaultMessage?: string;
  };
}

export const AddOnItem: FunctionComponent<AddOnItemProps> = ({
  item,
  listingPageDisplay = false,
}) => {
  const intl = useIntl();
  const backgroundImg = get(item, "image_ids[0]");
  const itemCategory = get(item, "category");
  const itemSubcategory = get(item, "subcategory");
  const uniqueSubcategory = includes(
    [
      "non-alcoholic_drinks",
      "alcoholic_drinks",
      "full_bar_package",
      "bar_services",
      "backdrops",
      "lighting",
      "props",
      "grips",
      "power_supplies",
      "ladder",
    ],
    itemSubcategory,
  );
  const itemIcon = uniqueSubcategory
    ? categoryIcons[itemSubcategory || ""]
    : categoryIcons[itemCategory || ""];

  const Icon = dynamic(
    () =>
      import(`@/icons/__generated__/${itemIcon}/${itemIcon}24`).catch(() => {
        // Fallback to 'Other' icon if the specified icon can't be loaded
        return import("@/icons/__generated__/Other");
      }),
    { loading: () => <div className="tw-w-96 tw-h-96" /> },
  );

  const priceOptionMessages = defineMessages({
    per_person: {
      id: "addons.utils.person",
      defaultMessage: "/person",
    },
    per_item: {
      id: "addons.utils.item",
      defaultMessage: "/item",
    },
    per_hour: {
      id: "addons.utils.item.hour",
      defaultMessage: "/hr",
    },
    flat_fee: {
      id: "addons.utils.flat_fee",
      defaultMessage: " flat fee",
    },
  });

  const priceOptionLabel: PriceOptionLabel = {
    per_person: priceOptionMessages.per_person,
    per_item: priceOptionMessages.per_item,
    per_hour: priceOptionMessages.per_hour,
    flat_fee: priceOptionMessages.flat_fee,
  };

  const unitCostBasis = () =>
    intl.formatMessage(priceOptionLabel[item.unit_cost_basis || ""]);

  const isVisible = get(item, "visible_on_listing");

  return (
    <div
      className="Addon tw-flex tw-items-center tw-pr-24 hover:tw-bg-gray-100"
      role="button"
      tabIndex={0}
    >
      <div
        className={`
        tw-relative tw-flex tw-items-center tw-justify-center tw-mr-16
        tw-min-h-[96px] tw-min-w-[96px] tw-rounded-[5px]
        ${!backgroundImg ? "tw-bg-gray-100" : ""}
        ${!backgroundImg && isVisible ? "tw-border tw-border-gray-300" : ""}
      `}
      >
        {backgroundImg ? (
          <Image
            src={backgroundImg}
            alt={item.label || "Add-on item"}
            fill
            sizes="96px"
            loader={cloudinaryImageLoader}
            className="tw-object-cover tw-rounded-[5px]"
          />
        ) : (
          <Icon />
        )}
      </div>

      <div className="ellipsis">
        <div className="label addon-title">
          <Text typeface="body-2-m">
            <FormattedMessage
              id="listing.add_on.item_label"
              defaultMessage="{label}"
              values={{
                label: item.label,
              }}
            />
          </Text>
        </div>
        <Text typeface="body-1" className=" tw-text-gray-600">
          {intl.formatNumber(item.unit_cost || 0, {
            currency: item.currency?.code || "USD",
            style: "currency",
          })}
          &nbsp;{unitCostBasis()}
        </Text>
        {!listingPageDisplay && (
          <div className="text-small ellipsis text-gray-600">
            {item.description}
          </div>
        )}
      </div>
    </div>
  );
};
